import { motion } from "framer-motion";
import { Button } from "../ui/button";
import { AdmissionFormInline } from "../forms/AdmissionFormInline";
import { ChevronRight, BookOpen, Users, Award, Shield } from "lucide-react";
import { Link } from "react-router-dom";

export const HeroSection = () => {
  return (
    <section className="relative min-h-[90vh] flex items-center" data-testid="hero-section">
      {/* Background Image */}
      <div 
        className="absolute inset-0 bg-cover bg-center bg-no-repeat"
        style={{
          backgroundImage: `url('https://images.unsplash.com/photo-1580582932707-520aed937b7b?auto=format&fit=crop&w=2000&q=80')`,
        }}
      >
        <div className="absolute inset-0 hero-overlay"></div>
      </div>

      {/* Admission Banner */}
      <div className="absolute top-0 left-0 right-0 bg-secondary text-white py-3 z-10">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <p className="text-sm md:text-base font-medium animate-pulse">
            Admissions Open for 2024-25 | Limited Seats Available | Apply Now!
          </p>
        </div>
      </div>

      {/* Content */}
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 w-full">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Left Content */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-left"
          >
            <div className="inline-flex items-center gap-2 bg-primary/10 text-primary px-4 py-2 rounded-full text-sm font-medium mb-6">
              <Award size={16} />
              <span>Excellence in Education Since 2005</span>
            </div>

            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 leading-tight mb-6">
              Welcome to{" "}
              <span className="text-primary">ID4U School</span>
            </h1>

            <p className="text-lg text-gray-600 mb-8 leading-relaxed max-w-xl">
              Nurturing young minds with quality education, modern facilities, and 
              experienced faculty. Building tomorrow's leaders with values, knowledge, 
              and innovation.
            </p>

            <div className="flex flex-wrap gap-4 mb-10">
              <Link to="/admission">
                <Button 
                  className="bg-primary hover:bg-primary/90 text-white rounded-full px-8 py-6 text-base font-semibold shadow-lg hover:shadow-xl transition-all"
                  data-testid="hero-apply-btn"
                >
                  Apply for Admission
                  <ChevronRight size={20} className="ml-2" />
                </Button>
              </Link>
              <Link to="/academics">
                <Button 
                  variant="outline"
                  className="border-2 border-primary text-primary hover:bg-primary/5 rounded-full px-8 py-6 text-base font-semibold"
                  data-testid="hero-explore-btn"
                >
                  <BookOpen size={20} className="mr-2" />
                  Explore Academics
                </Button>
              </Link>
            </div>

            {/* Quick Stats */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              {[
                { icon: Users, label: "Students", value: "2500+" },
                { icon: BookOpen, label: "Teachers", value: "120+" },
                { icon: Award, label: "Awards", value: "50+" },
                { icon: Shield, label: "Years", value: "19+" },
              ].map((stat, index) => (
                <motion.div
                  key={stat.label}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, delay: 0.6 + index * 0.1 }}
                  className="bg-white/80 backdrop-blur-sm rounded-xl p-4 text-center shadow-sm"
                >
                  <stat.icon className="w-6 h-6 text-secondary mx-auto mb-2" />
                  <div className="text-2xl font-bold text-primary">{stat.value}</div>
                  <div className="text-xs text-gray-600">{stat.label}</div>
                </motion.div>
              ))}
            </div>
          </motion.div>

          {/* Right - Admission Form */}
          <div className="lg:pl-8">
            <AdmissionFormInline />
          </div>
        </div>
      </div>
    </section>
  );
};
